import { PanelSettings, PostformingSideEnum } from 'cloud-shared-lib';
import * as _ from 'lodash';

import { CuttingButt } from '../models/cutting/cutting-butt.model';
import { AngleMode, OutlineAngle } from '../models/cutting/cutting-panel-outline.model';
import { PanelBase } from '../models/cutting/panel-base.model';
import {
  AngleEditor,
  OutlineEditorOptions,
  RestrictionMaxMin,
  RoundingParams,
} from '../models/user/outline-editor-options.model';

export class CuttingPanelHelper {
  static isEmptyPanel = (p: PanelBase): boolean => p.height * p.width * p.count <= 0;

  static checkPanelSize(
    panel: PanelBase,
    options: PanelSettings,
    defaultMaxHeight?: number,
    defaultMaxWidth?: number
  ): { valid: boolean; widthRange: RestrictionMaxMin; heightRange: RestrictionMaxMin } {
    const widthRange = CuttingPanelHelper.getPanelSizeRange(panel, options, 'width', defaultMaxWidth);
    const heightRange = CuttingPanelHelper.getPanelSizeRange(panel, options, 'height', defaultMaxHeight);

    const valid =
      CuttingPanelHelper.checkPanelSizeByRange(panel.width, panel.height, widthRange, heightRange) ||
      CuttingPanelHelper.checkPanelSizeByRange(panel.height, panel.width, widthRange, heightRange);

    return { valid, widthRange, heightRange };
  }

  private static checkPanelSizeByRange(
    width: number,
    height: number,
    widthRange: RestrictionMaxMin,
    heightRange: RestrictionMaxMin
  ): boolean {
    return height >= heightRange.min && width >= widthRange.min && height <= heightRange.max && width <= widthRange.max;
  }

  static getPanelSizeRange(
    panel: PanelBase,
    options: PanelSettings,
    side: 'width' | 'height',
    defaultMax = 10000
  ): RestrictionMaxMin {
    const defaultMin = side === 'width' ? options.minWidth : options.minHeight;

    const range = {
      min: Math.max(defaultMin, 0),
      max: defaultMax,
    };

    if (CuttingPanelHelper.hasFasteners(panel)) {
      const max = side === 'width' ? options.maxWidthWithHole : options.maxHeightWithHole;

      const min = side === 'width' ? options.minWidthWithHole : options.minHeightWithHole;

      range.min = Math.max(range.min, min);
      range.max = max || defaultMax;
    }

    if (CuttingPanelHelper.hasButts(panel)) {
      const min = side === 'width' ? options.minWidthWithButt : options.minHeightWithButt;

      range.min = Math.max(min, range.min);
    }

    if (CuttingPanelHelper.hasAngleEditing(panel) || CuttingPanelHelper.hasCutouts(panel)) {
      const min = side === 'width' ? options.minWidthWithCornerAndCutout : options.minHeightWithCornerAndCutout;
      const max = side === 'width' ? options.maxWidthWithCornerAndCutout : options.maxHeightWithCornerAndCutout;

      range.min = Math.max(min, range.min);
      range.max = max || defaultMax;
    }

    return range;
  }

  static hasButts(panel: PanelBase): boolean {
    return panel.l1 || panel.l2 || panel.w1 || panel.w2 ? true : false;
  }

  static hasAngleEditing(panel: PanelBase): boolean {
    return (
      panel.panelOutline.topLeft.mode !== AngleMode.None ||
      panel.panelOutline.topRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomRight.mode !== AngleMode.None ||
      panel.panelOutline.bottomLeft.mode !== AngleMode.None
    );
  }

  static hasCutouts(panel: PanelBase): boolean {
    return !_.isEmpty(panel.panelOutline.cutouts);
  }

  static hasFasteners(panel: PanelBase): boolean {
    return (
      panel.panelOutline.topSide.customHoles.length !== 0 ||
      panel.panelOutline.rightSide.customHoles.length !== 0 ||
      panel.panelOutline.bottomSide.customHoles.length !== 0 ||
      panel.panelOutline.leftSide.customHoles.length !== 0 ||
      panel.panelOutline.topSide.fastenersSets.length !== 0 ||
      panel.panelOutline.rightSide.fastenersSets.length !== 0 ||
      panel.panelOutline.bottomSide.fastenersSets.length !== 0 ||
      panel.panelOutline.leftSide.fastenersSets.length !== 0
    );
  }

  static valueInRangeMinMax(value: number, range: RestrictionMaxMin): boolean {
    return value >= range.min && value <= range.max;
  }

  static valueInRangeRouding(value: number, range: RoundingParams): boolean {
    return value >= range.min && value <= range.max;
  }

  static checkAngleOutlineValid(angle: OutlineAngle, angleOption: AngleEditor): boolean {
    switch (angle.mode) {
      case AngleMode.Rounded:
        return CuttingPanelHelper.valueInRangeRouding(angle.rounding, angleOption.rounding.restrictions);

      case AngleMode.RoundedInside:
        return CuttingPanelHelper.valueInRangeMinMax(angle.roundingInside, angleOption.insideRounding.restrictions);

      case AngleMode.Chamfer:
        return CuttingPanelHelper.valueInRangeMinMax(angle.chamferLength, angleOption.chamfer.restrictions);

      case AngleMode.Cutted:
        return (
          CuttingPanelHelper.valueInRangeMinMax(angle.cutX, angleOption.cutting.restrictions) &&
          CuttingPanelHelper.valueInRangeMinMax(angle.cutY, angleOption.cutting.restrictions)
        );

      case AngleMode.CuttedInside:
        return (
          CuttingPanelHelper.valueInRangeMinMax(angle.cutInsideX, angleOption.insideCutting.restrictions) &&
          CuttingPanelHelper.valueInRangeMinMax(angle.cutInsideY, angleOption.insideCutting.restrictions) &&
          angle.cutInsideR >= angleOption.insideCutting.restrictions.radiusMin &&
          angle.cutInsideR <= angleOption.insideCutting.restrictions.radiusMax
        );

      case AngleMode.CuttedInsideWithAngle:
        return (
          CuttingPanelHelper.valueInRangeMinMax(
            angle.cutInsideWithAngle.x,
            angleOption.insideCuttingWithAngle.restrictions
          ) &&
          CuttingPanelHelper.valueInRangeMinMax(
            angle.cutInsideWithAngle.y,
            angleOption.insideCuttingWithAngle.restrictions
          ) &&
          angle.cutInsideWithAngle.r >= angleOption.insideCuttingWithAngle.restrictions.radiusMin &&
          angle.cutInsideWithAngle.r <= angleOption.insideCuttingWithAngle.restrictions.radiusMax
        );
    }

    return true;
  }

  static checkAnglesPanelOutlineValid(panel: PanelBase, option: OutlineEditorOptions): boolean {
    let result = true;

    if (result && panel.panelOutline.topLeft.mode !== AngleMode.None) {
      result = result && CuttingPanelHelper.checkAngleOutlineValid(panel.panelOutline.topLeft, option.angles);
    }

    if (result && panel.panelOutline.topRight.mode !== AngleMode.None) {
      result = result && CuttingPanelHelper.checkAngleOutlineValid(panel.panelOutline.topRight, option.angles);
    }

    if (result && panel.panelOutline.bottomLeft.mode !== AngleMode.None) {
      result = result && CuttingPanelHelper.checkAngleOutlineValid(panel.panelOutline.bottomLeft, option.angles);
    }

    if (result && panel.panelOutline.bottomRight.mode !== AngleMode.None) {
      result = result && CuttingPanelHelper.checkAngleOutlineValid(panel.panelOutline.bottomRight, option.angles);
    }
    return result;
  }

  static checkButtsPanelOutlineValid(panel: PanelBase, postforming: PostformingSideEnum): boolean {
    let valid = true;

    if (panel.panelOutline.bottomLeft.mode === AngleMode.Rounded) {
      if (postforming == PostformingSideEnum.Bottom) {
        valid = valid && CuttingPanelHelper.compareButts(panel.panelOutline.bottomLeft.butt, panel.w1);
      } else {
        valid = valid && CuttingPanelHelper.compareButts(panel.panelOutline.bottomLeft.butt, panel.w1, panel.l2);
      }
    }

    if (panel.panelOutline.bottomRight.mode === AngleMode.Rounded) {
      if (postforming == PostformingSideEnum.Bottom) {
        valid = valid && CuttingPanelHelper.compareButts(panel.panelOutline.bottomRight.butt, panel.w2);
      } else {
        valid = valid && CuttingPanelHelper.compareButts(panel.panelOutline.bottomRight.butt, panel.l2, panel.w2);
      }
    }

    if (panel.panelOutline.topLeft.mode === AngleMode.Rounded) {
      if (postforming == PostformingSideEnum.TopAndBottom) {
        valid = valid && CuttingPanelHelper.compareButts(panel.panelOutline.topLeft.butt, panel.w1);
      } else {
        valid = valid && CuttingPanelHelper.compareButts(panel.panelOutline.topLeft.butt, panel.w1, panel.l1);
      }
    }

    if (panel.panelOutline.topRight.mode === AngleMode.Rounded) {
      if (postforming == PostformingSideEnum.TopAndBottom) {
        valid = valid && CuttingPanelHelper.compareButts(panel.panelOutline.topRight.butt, panel.w2);
      } else {
        valid = valid && CuttingPanelHelper.compareButts(panel.panelOutline.topRight.butt, panel.l1, panel.w2);
      }
    }
    return valid;
  }

  static compareButts(...butts: CuttingButt[]): boolean {
    if (!butts || butts.length < 2) {
      return true;
    }

    const compactedLength = _.compact(butts).length;
    if (compactedLength === 0) {
      return true;
    }
    if (compactedLength < butts.length) {
      return false;
    }

    const buttId = (name: string, sign: string) => name.toLowerCase().trim() + sign.toLowerCase().trim();

    const firstId = buttId(butts[0].name, butts[0].sign);

    return _.findIndex(butts, val => firstId !== buttId(val.name, val.sign), 1) === -1;
  }

  static insideCut = {
    findAngles(X: number, Y: number, X1: number, Y1: number): { YA: number; XA: number } {
      const rad2deg = (rad: number) => (rad * 180) / Math.PI;

      const YM = Y - Y1 < 0 ? 0 : 180;
      const XM = X - X1 < 0 ? 0 : 180;

      const YA = Math.abs(YM - rad2deg(Math.atan(X1 / Math.abs(Y - Y1))));
      const XA = Math.abs(XM - rad2deg(Math.atan(Y1 / Math.abs(X - X1))));

      return { YA, XA };
    },

    findSubSides(x: number, y: number, xA: number, yA: number): { y1: number; x1: number } {
      const deg2Rad = (val: number) => val * (Math.PI / 180);

      const xARad = deg2Rad(xA);
      const yARad = deg2Rad(270 - yA);

      const tgA = Math.tan(xARad);
      const tgB = Math.tan(yARad);

      if (tgA - tgB === 0) {
        throw new Error('Not found point');
      }

      const sign = xA < 90 ? 1 : -1;

      let x1 = (y + sign * Math.abs(x * tgA)) / (tgA - tgB);

      if (xA === 90) {
        x1 = Math.abs(x1);
      }

      const y1 = tgB * x1 + y;

      return { x1, y1 };
    },

    getRoundingParams(
      X: number,
      Y: number,
      R: number,
      angleX: number,
      angleY: number
    ): {
      cx: number;
      cy: number;
      x2: number;
      x3: number;
      y2: number;
      y3: number;
    } {
      const deg2rad = (deg: number) => deg * (Math.PI / 180);

      const sign = angleX < 90 ? 1 : -1;

      const tgA: number = Math.tan(deg2rad(angleX));
      const tgB: number = Math.tan(deg2rad(270 - angleY));
      const { x1, y1 } = this.findSubSides(X, Y, angleX, angleY);

      const lambda: number = Math.sqrt((x1 ** 2 + Math.pow(y1 - Y, 2)) / (Math.pow(x1 - X, 2) + y1 ** 2));

      const XM: number = (lambda * X) / (1 + lambda);
      const YM: number = (Y + lambda * 0) / (1 + lambda);

      const shift: number = YM + (1 / tgB) * XM; // для перпендикуляра к верхней наклонной 1/tgB - коэфф наклона верхней
      const tgbis: number = (y1 - YM) / (x1 - XM);
      const offbis: number = y1 - tgbis * x1;

      const xG: number = (Y - shift) / (-1 / tgB + sign * tgB);
      const yG: number = tgB * xG + Y;

      const xH: number = xG + R * Math.cos(Math.atan(-1 / tgB));
      const yH: number = yG + R * Math.sin(Math.atan(-1 / tgB));

      const c: number = yH - xH * tgB;

      let cx: number = (c - offbis) / (tgbis - tgB);
      let cy: number = tgbis * cx + offbis;

      if (angleY >= 90) {
        cx = 2 * x1 - cx;
        cy = 2 * y1 - cy;
      }

      const sd = -tgA * X;

      let A = 1 + tgB * tgB;
      let B = 2 * (tgB * Y - cx - tgB * cy);
      let C = cx * cx + Y * Y + cy * cy - 2 * Y * cy - R * R;
      let discriminant = Math.round(B * B - 4 * A * C);

      let x2 = 0;
      let y2 = 0;

      if (discriminant >= 0) {
        x2 = (-B - Math.sqrt(discriminant)) / (2 * A);
        y2 = x2 * tgB + Y;
      } else throw new Error('Невозможно реализовать скругление');

      A = 1 + tgA ** 2;
      B = 2 * sd * tgA - 2 * cx - 2 * tgA * cy;
      C = cx ** 2 + cy ** 2 - 2 * sd * cy + sd ** 2 - R ** 2;
      discriminant = Math.round(B ** 2 - 4 * A * C);

      let x3 = 0;
      let y3 = 0;

      if (angleX === 90) {
        x3 = X;
        y3 = y1 - Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
      } else if (discriminant >= 0) {
        x3 = (-B - Math.sqrt(discriminant)) / (2 * A);
        y3 = x3 * tgA + sd;
      } else throw new Error('Невозможно реализовать скругление');

      return { cx, cy, x2, x3, y2, y3 };
    },
  };
}
